import {
  AssessmentResultTypeComponent,
  type AssessmentResult,
  type Candidate,
} from '~/types/__generated__'

export function useAssessmentCognitive() {
  function extractCognitiveAssessmentResultFromCanidate(candidate: Candidate) {
    return candidate?.assessmentResults?.find(
      (assessmentResult) =>
        assessmentResult?.type?.resultType ===
        AssessmentResultTypeComponent.resultType.COGNITIVE,
    )
  }

  function extractMostAccurateCognitiveLevelFromAssessmentResult(
    assessmentResult: AssessmentResult,
  ) {
    const mostAccurateCognitiveLevel = assessmentResult?.data?.reduce(
      (mostAccurateLevel: any, data: any) => {
        if (data.name === 'IQ') return mostAccurateLevel
        if (mostAccurateLevel === null) return data
        if (
          Math.abs(5.5 - data.assessmentScore) <
          Math.abs(5.5 - mostAccurateLevel.assessmentScore)
        )
          return data
        return mostAccurateLevel
      },
      null,
    )
    return mostAccurateCognitiveLevel?.name?.toUpperCase()
  }

  function extractEducationLevelsFromAssessmentResult(
    assessmentResult: AssessmentResult,
  ) {
    if (!assessmentResult.data?.length) return {}

    // Average sten score for each education level
    const goal = 5.5
    const data = assessmentResult.data.filter((data) => data.name !== 'IQ')

    const abstractScores = data.map((item) => {
      return {
        name: item.name,
        assessmentScore: item?.subdata?.filter(
          (sub) => sub.name === 'abstract',
        )[0]?.assessmentScore,
      }
    })

    const numericScores = data.map((item) => {
      return {
        name: item.name,
        assessmentScore: item?.subdata?.filter(
          (sub) => sub.name === 'numeric',
        )[0]?.assessmentScore,
      }
    })

    const verbalScores = data.map((item) => {
      return {
        name: item.name,
        assessmentScore: item?.subdata?.filter(
          (sub) => sub.name === 'verbal',
        )[0]?.assessmentScore,
      }
    })

    const getMostAccurateLevel = (data: any) => {
      const goal = 5.5
      const maxScore = 10.5
      const minScore = 0.5

      const maxLevel = 'wo'
      const minLevel = 'vmbo'

      if (data.every((item) => item.assessmentScore === maxScore))
        return data.find((item) => item.name === maxLevel)
      if (data.every((item) => item.assessmentScore === minScore))
        return data.find((item) => item.name === minLevel)
      return data.reduce((prev, curr) =>
        Math.abs(curr.assessmentScore - goal) <=
        Math.abs(prev.assessmentScore - goal)
          ? curr
          : prev,
      )
    }

    const overall = getMostAccurateLevel(data)
    const abstract = getMostAccurateLevel(abstractScores)
    const numeric = getMostAccurateLevel(numericScores)
    const verbal = getMostAccurateLevel(verbalScores)

    return {
      overall,
      abstract,
      numeric,
      verbal,
    }
  }

  function extractGeneralScoresFromAssessmentResult(
    assessmentResult: AssessmentResult,
  ) {
    if (!assessmentResult.data?.length) return null

    const iq = assessmentResult.data.find((data) => data.name === 'IQ')

    const abstract = iq?.subdata?.find((subdata) => subdata.name === 'abstract')
    const numeric = iq?.subdata?.find((subdata) => subdata.name === 'numeric')
    const verbal = iq?.subdata?.find((subdata) => subdata.name === 'verbal')

    return {
      total: iq?.assessmentScore?.toFixed(2),
      abstract: abstract?.assessmentScore?.toFixed(2),
      numeric: numeric?.assessmentScore?.toFixed(2),
      verbal: verbal?.assessmentScore?.toFixed(2),
    }
  }

  function extractMostAccurateCognitiveLevelFromCandidate(
    candidate: Candidate,
  ) {
    const cognitiveAssessment =
      extractCognitiveAssessmentResultFromCanidate(candidate)
    if (!cognitiveAssessment) return null
    return extractMostAccurateCognitiveLevelFromAssessmentResult(
      cognitiveAssessment,
    )
  }

  function extractEducationLevelsFromCandidate(candidate: Candidate) {
    const cognitiveAssessment =
      extractCognitiveAssessmentResultFromCanidate(candidate)
    if (!cognitiveAssessment) return null
    return extractEducationLevelsFromAssessmentResult(cognitiveAssessment)
  }

  function extractGeneralScoresFromCandidate(candidate: Candidate) {
    const cognitiveAssessment =
      extractCognitiveAssessmentResultFromCanidate(candidate)
    if (!cognitiveAssessment) return null
    return extractGeneralScoresFromAssessmentResult(cognitiveAssessment)
  }

  return {
    extractCognitiveAssessmentResultFromCanidate,

    extractMostAccurateCognitiveLevelFromAssessmentResult,
    extractEducationLevelsFromAssessmentResult,
    extractGeneralScoresFromAssessmentResult,

    extractMostAccurateCognitiveLevelFromCandidate,
    extractEducationLevelsFromCandidate,
    extractGeneralScoresFromCandidate,
  }
}
